import React, { useState } from 'react';
import MentionInput from './MentionInput';
import { getHubSpotContext } from '../utils/hubspotContext';
import './MessageInput.css';

function MessageInput({ onSendMessage, users, channelId, channelName, token, threadTs }) {
  const [message, setMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleSend = async (text, mentions) => {
    // Only proceed if there's text or files to send
    if (!text.trim() && attachedFiles.length === 0) return;

    setIsUploading(true);

    try {
      // If there are files to upload
      if (attachedFiles.length > 0) {
        const uploadedFiles = [];
        for (const file of attachedFiles) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('channels', channelId);
          if (threadTs) {
            formData.append('thread_ts', threadTs);
          }
          // Add the message as initial_comment if there's text
          if (text.trim()) {
            formData.append('initial_comment', text);
          }

          // Add HubSpot sync data if enabled
          // const hubspotSyncEnabled = localStorage.getItem('hubspot_sync_enabled') === 'true';
          // const hubspotContext = getHubSpotContext();
          // if (hubspotSyncEnabled && hubspotContext) {
          //   formData.append('hubspotSync', JSON.stringify({
          //     enabled: true,
          //     portalId: hubspotContext.portalId,
          //     userId: hubspotContext.userId,
          //     contactId: hubspotContext.contactId,
          //     channelName: channelName || channelId
          //   }));
          // }

          const token = localStorage.getItem('slack_user_token');
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
          const response = await fetch(`${BACKEND_URL}/api/files/upload`, {
            method: 'POST',
            headers: {
              'x-slack-token': token
            },
            body: formData,
            credentials: 'include'
          });

          if (response.ok) {
            const data = await response.json();
            uploadedFiles.push(data.file);
          } else {
            throw new Error(`Failed to upload ${file.name}`);
          }
        }

        // After uploading files, trigger message refresh or emit event
        // The file upload already posts to the channel, so we need to trigger a refresh
        if (uploadedFiles.length > 0) {
          // Call onSendMessage with a special flag or empty message to trigger refresh
          // This will make the parent component refresh the message list
          onSendMessage('', mentions, uploadedFiles);
        }
      } else if (text.trim()) {
        // Only text, no files
        onSendMessage(text, mentions);
      }

      // Clear everything after successful send
      setMessage('');
      setAttachedFiles([]);
    } catch (error) {
      console.error('Error sending message with files:', error);
      alert('Failed to send message with files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Validate file sizes
    for (const file of files) {
      if (file.size > 100 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 100MB.`);
        continue;
      }
      // Add file to local state (not uploading yet)
      setAttachedFiles(prev => [...prev, file]);
    }

    // Reset file input
    if (event.target) {
      event.target.value = '';
    }
  };

  const removeAttachment = (fileName) => {
    setAttachedFiles(prev => prev.filter(f => f.name !== fileName));
  };

  console.log('Attached files:', attachedFiles);

  return (
    <div className="message-input-container">
      {attachedFiles.length > 0 && (
        <div className="attached-files-preview">
          {attachedFiles.map((file, index) => {
            const isImage = file.type.startsWith('image/');
            const imageUrl = isImage ? URL.createObjectURL(file) : null;

            return (
              <div key={`${file.name}-${index}`} className={`attached-file-item ${isImage ? 'attached-image-item' : ''}`}>
                {isImage ? (
                  <div className="image-preview-wrapper">
                    <img
                      src={imageUrl}
                      alt={file.name}
                      className="attached-image-preview"
                      onLoad={() => URL.revokeObjectURL(imageUrl)}
                    />
                    <button
                      className="remove-image-attachment"
                      onClick={() => removeAttachment(file.name)}
                      title="Remove image"
                      disabled={isUploading}
                    >
                      ×
                    </button>
                    <div className="image-file-info">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="file-icon">📎</span>
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
                    <button
                      className="remove-attachment"
                      onClick={() => removeAttachment(file.name)}
                      title="Remove attachment"
                      disabled={isUploading}
                    >
                      ×
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="message-input">
        <MentionInput
          value={message}
          onChange={setMessage}
          onSend={handleSend}
          users={users}
          channelId={channelId}
          token={token}
          placeholder={isUploading ? "Uploading..." : "Type a message"}
          onFileSelect={handleFileSelect}
          isUploading={isUploading}
          hasAttachments={attachedFiles.length > 0}
          enableFormatting={true}
        />
      </div>
    </div>
  );
}

export default MessageInput;